import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FileText, DollarSign, TrendingUp, Package, Users, RefreshCw, Search, X, MapPin, ChevronDown } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import KPICard from 'components/common/KPICard';
import DataTable from 'components/common/DataTable';
import EntradasNFTable from './Components/EntradasNFTable';
import CustomPieChart from 'components/common/CustomPieChart';
import CustomBarChart from 'components/common/CustomBarChart';
import EnhancedLegend from 'components/common/EnhancedLegend';
import Pagination from 'components/Pagination';
import produtosUsadosOSService from 'features/estoque/services/produtosUsadosOSService';
import { DateRangeFilterBlur } from 'components/filters';
import NotificationToast from 'components/common/NotificationToast';
import PeriodoDisplay from '../PeriodoDisplay';

const MovimentacaoInterna = () => {
  // Filtro de data (mês até hoje por padrão)
  const today = new Date();
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const pad = (n) => String(n).padStart(2, '0');
  const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const [range, setRange] = useState({ presetId: 'mtd', start: toISO(startOfMonth), end: toISO(today) });
  const [tipoAtivo, setTipoAtivo] = useState('entrada'); // 'entrada' ou 'saida'
  const [dadosEntrada, setDadosEntrada] = useState({});
  const [dadosSaida, setDadosSaida] = useState({});
  const [loading, setLoading] = useState(false);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState(null);
  
  // Usar loading interno
  const isLoading = loading;
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [classificacaoRanking, setClassificacaoRanking] = useState('produtos'); // 'produtos' ou 'fornecedores'
  const [limparCacheLoading, setLimparCacheLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [cidadesSelecionadas, setCidadesSelecionadas] = useState([]);
  const [cidadesSelecionadasDraft, setCidadesSelecionadasDraft] = useState([]);
  const [dropdownCidadesAberto, setDropdownCidadesAberto] = useState(false);
  const [buscaCidade, setBuscaCidade] = useState('');
  const dropdownCidadesRef = useRef(null);
  const rangeRef = useRef(range);
  const pendingRequestsRef = useRef(0);
  const entradaRequestIdRef = useRef(0);
  const saidaRequestIdRef = useRef(0);

  useEffect(() => {
    rangeRef.current = range;
  }, [range]);

  const iniciarRequisicao = useCallback(() => {
    pendingRequestsRef.current += 1;
    setLoading(true);
  }, []);

  const finalizarRequisicao = useCallback(() => {
    pendingRequestsRef.current = Math.max(0, pendingRequestsRef.current - 1);
    if (pendingRequestsRef.current === 0) {
      setLoading(false);
    }
  }, []);
  
  // Função para limpar cache de ambos endpoints e recarregar
  const limparCacheERecarregar = async () => {
    setLimparCacheLoading(true);
    try {
      const params = {
        data_inicio: range.start,
        data_fim: range.end
      };
      
      // Limpar cache de entrada
      const responseEntrada = await produtosUsadosOSService.limparCacheGenerico('movimentacao-interna-entrada', params);
      // Limpar cache de saída
      const responseSaida = await produtosUsadosOSService.limparCacheGenerico('movimentacao-interna-saida', params);
      
      // Verificar sucesso
      if (responseEntrada?.success && responseSaida?.success) {
        const message = 'Dados recarregados com sucesso!';
        setNotification({ message, type: 'success' });
        
        // Recarregar ambos
        await carregarDadosEntrada();
        await carregarDadosSaida();
      } else {
        const errorMsg = responseEntrada?.message || responseSaida?.message || 'Erro ao recarregar dados';
        setNotification({ message: errorMsg, type: 'error' });
      }
    } catch (error) {
      console.error('❌ Erro ao recarregar dados:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao recarregar dados';
      setNotification({ message: errorMessage, type: 'error' });
    } finally {
      setLimparCacheLoading(false);
    }
  };


  // Carregar dados de entrada
  const carregarDadosEntrada = useCallback(async (rangeOverride = null) => {
    const requestId = ++entradaRequestIdRef.current;
    try {
      iniciarRequisicao();
      const rangeToUse = rangeOverride || rangeRef.current;
      const response = await produtosUsadosOSService.buscarMovimentacaoInternaEntrada({
        data_inicio: rangeToUse.start,
        data_fim: rangeToUse.end,
      });
      
      // Verificar se a resposta tem a estrutura SWR
      if (response?.data) {
        if (requestId !== entradaRequestIdRef.current) return;
        // Se tem estrutura SWR (com success, data, cached, etc.)
        if (response.success && response.data) {
          setDadosEntrada(response.data.data);
        } else {
          // Fallback para estrutura antiga
          setDadosEntrada(response.data.data);
        }
      }
    } catch (error) {
      console.error('❌ Erro ao carregar dados de entrada:', error);
    } finally {
      if (requestId === entradaRequestIdRef.current) {
        setUltimaAtualizacao(new Date());
      }
      finalizarRequisicao();
    }
  }, [finalizarRequisicao, iniciarRequisicao]);

  // Carregar dados de saída
  const carregarDadosSaida = useCallback(async (rangeOverride = null) => {
    const requestId = ++saidaRequestIdRef.current;
    try {
      iniciarRequisicao();
      const rangeToUse = rangeOverride || rangeRef.current;
      const response = await produtosUsadosOSService.buscarMovimentacaoInternaSaida({
        data_inicio: rangeToUse.start,
        data_fim: rangeToUse.end,
      });
      
      // Verificar se a resposta tem a estrutura SWR
      if (response?.data) {
        if (requestId !== saidaRequestIdRef.current) return;
        // Se tem estrutura SWR (com success, data, cached, etc.)
        if (response.success && response.data) {
          setDadosSaida(response.data.data);
        } else {
          // Fallback para estrutura antiga
          setDadosSaida(response.data.data);
        }
      }
    } catch (error) {
      console.error('❌ Erro ao carregar dados de saída:', error);
    } finally {
      if (requestId === saidaRequestIdRef.current) {
        setUltimaAtualizacao(new Date());
      }
      finalizarRequisicao();
    }
  }, [finalizarRequisicao, iniciarRequisicao]);
  
  // Carregar dados quando o intervalo muda
  useEffect(() => {
    carregarDadosEntrada(range);
    carregarDadosSaida(range);
  }, [range, carregarDadosEntrada, carregarDadosSaida]);

  useEffect(() => {
    const handleCompanyChange = () => {
      carregarDadosEntrada();
      carregarDadosSaida();
    };

    window.addEventListener('company-changed', handleCompanyChange);
    window.addEventListener('company-context-changed', handleCompanyChange);

    return () => {
      window.removeEventListener('company-changed', handleCompanyChange);
      window.removeEventListener('company-context-changed', handleCompanyChange);
    };
  }, [carregarDadosEntrada, carregarDadosSaida]);

  useEffect(() => {
    const selecoesIguais = (a = [], b = []) => (
      a.length === b.length && a.every((item) => b.includes(item))
    );

    const aplicarCidadesSeMudou = () => {
      if (!selecoesIguais(cidadesSelecionadas, cidadesSelecionadasDraft)) {
        setCidadesSelecionadas(cidadesSelecionadasDraft);
      }
    };

    const handler = (e) => {
      if (dropdownCidadesRef.current && !dropdownCidadesRef.current.contains(e.target)) {
        if (dropdownCidadesAberto) {
          aplicarCidadesSeMudou();
        }
        setDropdownCidadesAberto(false);
      }
    };

    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [cidadesSelecionadas, cidadesSelecionadasDraft, dropdownCidadesAberto]);

  useEffect(() => {
    setPage(1);
  }, [tipoAtivo, cidadesSelecionadas]);



  // Removido LoadingSpinner para permitir blur do DateRangeFilterBlur

  // Dados ativos baseado no tipo selecionado
  const dadosAtivos = tipoAtivo === 'entrada' ? dadosEntrada : dadosSaida;
  const tipoLabel = tipoAtivo === 'entrada' ? 'Entrada' : 'Saída';

  const extrairLista = useCallback((bloco, chavesPreferenciais = []) => {
    if (!bloco) return [];
    if (Array.isArray(bloco)) {
      // Caso comum da API: [{ entradas: [...] }] ou [{ saidas: [...] }]
      if (bloco.length === 1 && bloco[0] && typeof bloco[0] === 'object' && !Array.isArray(bloco[0])) {
        const itemUnico = bloco[0];
        for (const chave of chavesPreferenciais) {
          const valor = itemUnico?.[chave];
          if (Array.isArray(valor)) return valor;
        }
        if (Array.isArray(itemUnico?.data)) return itemUnico.data;
        if (Array.isArray(itemUnico?.oss)) return itemUnico.oss;
      }
      return bloco;
    }

    for (const chave of chavesPreferenciais) {
      const valor = bloco?.[chave];
      if (Array.isArray(valor)) return valor;
    }

    if (Array.isArray(bloco?.data)) return bloco.data;
    if (Array.isArray(bloco?.oss)) return bloco.oss;

    return [];
  }, []);

  const normalizarTexto = (valor) => String(valor || '').trim().toLowerCase();
  const parseNumero = useCallback((valor) => {
    if (valor === null || valor === undefined) return 0;
    if (typeof valor === 'number') return Number.isFinite(valor) ? valor : 0;
    const texto = String(valor).trim();
    if (!texto) return 0;

    // Formato "1234.56" (decimal com ponto) - comum no retorno da API
    if (/^-?\d+(\.\d+)?$/.test(texto)) {
      const parsedPonto = Number(texto);
      return Number.isFinite(parsedPonto) ? parsedPonto : 0;
    }

    // Formato brasileiro com milhar e decimal: "1.234,56"
    if (/^-?\d{1,3}(\.\d{3})+(,\d+)?$/.test(texto)) {
      const parsedBr = Number(texto.replace(/\./g, '').replace(',', '.'));
      return Number.isFinite(parsedBr) ? parsedBr : 0;
    }

    // Formato "1234,56" (decimal com vírgula)
    if (/^-?\d+(,\d+)?$/.test(texto)) {
      const parsedVirgula = Number(texto.replace(',', '.'));
      return Number.isFinite(parsedVirgula) ? parsedVirgula : 0;
    }

    // Fallback defensivo para strings com ruído (R$, espaços, etc.)
    const somenteNumeros = texto.replace(/[^\d,.-]/g, '');
    const ultimoPonto = somenteNumeros.lastIndexOf('.');
    const ultimaVirgula = somenteNumeros.lastIndexOf(',');
    let normalizado = somenteNumeros;

    if (ultimoPonto > -1 && ultimaVirgula > -1) {
      // Se os dois existem, o último separador tende a ser decimal
      if (ultimoPonto > ultimaVirgula) {
        normalizado = somenteNumeros.replace(/,/g, '');
      } else {
        normalizado = somenteNumeros.replace(/\./g, '').replace(',', '.');
      }
    } else if (ultimaVirgula > -1) {
      normalizado = somenteNumeros.replace(',', '.');
    }

    const parsed = Number(normalizado);
    return Number.isFinite(parsed) ? parsed : 0;
  }, []);
  const cidadesSelecionadasNormalizadas = useMemo(
    () => new Set((cidadesSelecionadas || []).map(normalizarTexto).filter(Boolean)),
    [cidadesSelecionadas]
  );
  const temFiltroCidade = cidadesSelecionadasNormalizadas.size > 0;
  const getCidadeItem = (item) => item?.CIDADE || item?.cidade || item?.Cidade || '';
  const itemPassaCidade = useCallback((item) => {
    if (!temFiltroCidade) return true;
    const cidade = normalizarTexto(getCidadeItem(item));
    return cidade && cidadesSelecionadasNormalizadas.has(cidade);
  }, [temFiltroCidade, cidadesSelecionadasNormalizadas]);

  // Dados para gráficos
  const kpis = useMemo(() => dadosAtivos.kpis || {}, [dadosAtivos.kpis]);
  const topProdutosRaw = extrairLista(dadosAtivos.top_comprados, ['top_10', 'top10', 'data']);
  const topFornecedoresRaw = extrairLista(dadosAtivos.top_fornecedores, ['top_10', 'top10', 'data']);
  const detalhamentoRaw = extrairLista(dadosAtivos.detalhamento, ['detalhamento', 'data']);
  const detalhamentoCompletoRaw = extrairLista(dadosAtivos.detalhamento_completo, ['detalhamento', 'data']);
  // Usar 'entradas' ou 'saidas' dependendo do tipo
  const porDocumentoRaw = tipoAtivo === 'entrada'
    ? extrairLista(dadosAtivos.por_documento, ['entradas', 'entrada', 'data'])
    : extrairLista(dadosAtivos.por_documento, ['saidas', 'saida', 'data']);
  const porOperadorRaw = tipoAtivo === 'entrada'
    ? extrairLista(dadosAtivos.por_operador, ['entradas', 'entrada', 'data'])
    : extrairLista(dadosAtivos.por_operador, ['saidas', 'saida', 'data']);
  const porMesRaw = tipoAtivo === 'entrada'
    ? extrairLista(dadosAtivos.por_mes, ['entradas', 'entrada', 'data'])
    : extrairLista(dadosAtivos.por_mes, ['saidas', 'saida', 'data']);

  const cidadesDisponiveis = useMemo(() => {
    const cidades = new Set();
    const fontes = [
      ...topProdutosRaw,
      ...topFornecedoresRaw,
      ...detalhamentoRaw,
      ...detalhamentoCompletoRaw,
      ...porDocumentoRaw,
      ...porOperadorRaw,
      ...porMesRaw
    ];
    fontes.forEach((item) => {
      const cidade = getCidadeItem(item);
      if (cidade) cidades.add(cidade);
    });
    return Array.from(cidades).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [topProdutosRaw, topFornecedoresRaw, detalhamentoRaw, detalhamentoCompletoRaw, porDocumentoRaw, porOperadorRaw, porMesRaw]);

  const cidadesFiltradas = useMemo(() => {
    if (!buscaCidade.trim()) return cidadesDisponiveis;
    const termo = buscaCidade.toLowerCase().trim();
    return cidadesDisponiveis.filter((cidade) => cidade.toLowerCase().includes(termo));
  }, [cidadesDisponiveis, buscaCidade]);

  const topProdutos = useMemo(() => topProdutosRaw.filter(itemPassaCidade), [topProdutosRaw, itemPassaCidade]);
  const topFornecedores = useMemo(() => topFornecedoresRaw.filter(itemPassaCidade), [topFornecedoresRaw, itemPassaCidade]);
  const detalhamentoNormalizado = useMemo(() => {
    return detalhamentoRaw.map((item) => ({
      ...item,
      'ID ENTRADA': item?.['ID ENTRADA'] ?? item?.ID_ENTRADA ?? item?.id_entrada ?? item?.idEntrada ?? item?.ID ?? item?.id ?? '',
      'ID SAIDA': item?.['ID SAIDA'] ?? item?.ID_SAIDA ?? item?.id_saida ?? item?.idSaida ?? item?.ID ?? item?.id ?? ''
    }));
  }, [detalhamentoRaw]);
  const detalhamentoCompletoNormalizado = useMemo(() => {
    return detalhamentoCompletoRaw.map((item) => ({
      ...item,
      'ID ENTRADA': item?.['ID ENTRADA'] ?? item?.ID_ENTRADA ?? item?.id_entrada ?? item?.idEntrada ?? item?.ID ?? item?.id ?? '',
      'ID SAIDA': item?.['ID SAIDA'] ?? item?.ID_SAIDA ?? item?.id_saida ?? item?.idSaida ?? item?.ID ?? item?.id ?? ''
    }));
  }, [detalhamentoCompletoRaw]);
  const detalhamento = useMemo(() => detalhamentoNormalizado.filter(itemPassaCidade), [detalhamentoNormalizado, itemPassaCidade]);
  const detalhamentoCompleto = useMemo(() => detalhamentoCompletoNormalizado.filter(itemPassaCidade), [detalhamentoCompletoNormalizado, itemPassaCidade]);
  const porDocumento = useMemo(() => porDocumentoRaw.filter(itemPassaCidade), [porDocumentoRaw, itemPassaCidade]);
  const porOperador = useMemo(() => porOperadorRaw.filter(itemPassaCidade), [porOperadorRaw, itemPassaCidade]);
  const porMes = useMemo(() => porMesRaw.filter(itemPassaCidade), [porMesRaw, itemPassaCidade]);
  
  const kpisFiltrados = useMemo(() => {
    if (!temFiltroCidade) {
      return {
        total_nf: parseNumero(kpis.total_nf || 0),
        total_valor: parseNumero(kpis.total_valor || 0),
        total_movimentacao: tipoAtivo === 'entrada'
          ? parseNumero(kpis.total_entrada || kpis.total_entradas || 0)
          : parseNumero(kpis.total_saida || 0)
      };
    }

    const kpisPorCidade = Array.isArray(kpis?.por_cidade) ? kpis.por_cidade : [];
    if (kpisPorCidade.length > 0) {
      const agregados = kpisPorCidade
        .filter((item) => {
          const cidade = normalizarTexto(item?.cidade ?? item?.CIDADE ?? '');
          return cidade && cidadesSelecionadasNormalizadas.has(cidade);
        })
        .reduce((acc, item) => {
          acc.total_nf += parseNumero(item?.total_nf ?? item?.TOTAL_NF ?? 0);
          acc.total_valor += parseNumero(item?.total_valor ?? item?.TOTAL_VALOR ?? 0);
          if (tipoAtivo === 'entrada') {
            acc.total_movimentacao += parseNumero(item?.total_entradas ?? item?.total_entrada ?? item?.TOTAL_ENTRADAS ?? 0);
          } else {
            acc.total_movimentacao += parseNumero(item?.total_saida ?? item?.total_saidas ?? item?.TOTAL_SAIDA ?? 0);
          }
          return acc;
        }, { total_nf: 0, total_valor: 0, total_movimentacao: 0 });

      return agregados;
    }

    const idsUnicos = new Set();
    let totalValor = 0;
    let totalMov = 0;
    const idChave = tipoAtivo === 'entrada' ? 'ID ENTRADA' : 'ID SAIDA';
    const origem = detalhamentoCompleto.length > 0 ? detalhamentoCompleto : detalhamento;

    origem.forEach((item) => {
      const id = item?.[idChave] ?? item?.ID ?? item?.id ?? '';
      if (id !== '') idsUnicos.add(String(id));
      totalValor += parseNumero(item?.['VALOR NF'] ?? item?.['VALOR TOTAL'] ?? item?.TOTAL ?? 0);
      totalMov += parseNumero(item?.QUANTIDADE ?? item?.QTD ?? 0);
    });

    if (totalMov === 0) {
      totalMov = idsUnicos.size;
    }

    return {
      total_nf: idsUnicos.size,
      total_valor: totalValor,
      total_movimentacao: totalMov
    };
  }, [temFiltroCidade, kpis, tipoAtivo, detalhamentoCompleto, detalhamento, parseNumero, cidadesSelecionadasNormalizadas]);

  // Valor total baseado no tipo
  const totalMovimentacao = kpisFiltrados.total_movimentacao;

  // Paginação para detalhamento
  const totalPages = Math.ceil(detalhamento.length / itemsPerPage);
  const startIndex = (page - 1) * itemsPerPage;
  const detalhamentoPaginado = detalhamento.slice(startIndex, startIndex + itemsPerPage);

  // Definir o campo de ID baseado no tipo
  const idField = tipoAtivo === 'entrada' ? 'ID ENTRADA' : 'ID SAIDA';
  const idLabel = tipoAtivo === 'entrada' ? 'ID ENTRADA' : 'ID SAIDA';

  // Usar useMemo para recriar as colunas quando tipoAtivo mudar
  const columnsDetalhamento = useMemo(() => [
    {
      key: idField,
      label: idLabel,
      sortable: true,
      className: 'w-[15%]'
    },
    {
      key: 'DATA',
      label: 'Data',
      sortable: true,
      className: 'w-[15%]'
    },
    {
      key: 'DOCUMENTO',
      label: 'Documento',
      sortable: true,
      className: 'w-[50%]'
    },
    {
      key: 'VALOR TOTAL',
      label: 'Valor Total',
      sortable: true,
      className: 'w-[20%] text-right',
      render: (value) => (
        <span className="font-bold text-[color:var(--status-good)] text-xs tabular-nums">
          R$ {parseFloat(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
        </span>
      )
    }
  ], [idField, idLabel]);

  const _columnsDetalhamentoCompleto = useMemo(() => [
    {
      key: idField,
      label: idLabel,
      sortable: true
    },
    {
      key: 'DATA',
      label: 'Data',
      sortable: true
    },
    {
      key: 'FUNCIONARIO',
      label: 'Funcionário',
      sortable: true
    },
    {
      key: 'SETOR',
      label: 'Setor',
      sortable: true
    },
    {
      key: 'PRODUTO',
      label: 'Produto',
      sortable: true
    },
    {
      key: 'DOCUMENTO',
      label: 'Documento',
      sortable: true
    },
    {
      key: 'FORNECEDOR',
      label: 'Fornecedor',
      sortable: true
    },
    {
      key: 'QUANTIDADE',
      label: 'Quantidade',
      sortable: true,
      className: 'text-right',
      render: (value) => (
        <span className="font-medium text-[color:var(--text-primary)] tabular-nums">
          {Math.round(Number(value || 0)).toLocaleString('pt-BR', { 
            minimumFractionDigits: 0, 
            maximumFractionDigits: 0 
          })}
        </span>
      )
    },
    {
      key: 'VALOR UNITÁRIO',
      label: 'Valor Unitário',
      sortable: true,
      className: 'text-right',
      render: (value) => (
        <span className="font-bold text-[color:var(--accent)] tabular-nums">
          R$ {parseFloat(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
        </span>
      )
    },
    {
      key: 'VALOR NF',
      label: 'Valor NF',
      sortable: true,
      className: 'text-right',
      render: (value) => (
        <span className="font-bold text-[color:var(--status-good)] tabular-nums">
          R$ {parseFloat(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
        </span>
      )
    }
  ], [idField, idLabel]);

  // Dados para gráficos
  const dadosGraficoDocumento = porDocumento.map(item => ({
    name: item.DOCUMENTO || item.documento || 'Sem documento',
    value: Number(item.TOTAL ?? item.total ?? 0)
  }));

  const dadosGraficoDocumentoAgregado = useMemo(() => {
    const mapa = new Map();
    dadosGraficoDocumento.forEach((item) => {
      const nome = String(item.name || 'Sem documento');
      const atual = mapa.get(nome) || 0;
      mapa.set(nome, atual + Number(item.value || 0));
    });

    return Array.from(mapa.entries())
      .map(([name, value]) => ({
        name: name.length > 30 ? `${name.substring(0, 30)}...` : name,
        value
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  }, [dadosGraficoDocumento]);

  const dadosGraficoOperador = porOperador.map(item => ({
    name: item.OPERADOR || item.operador || 'Sem operador',
    value: Number(item.TOTAL ?? item.total ?? 0)
  }));

  const dadosGraficoOperadorAgregado = useMemo(() => {
    const mapa = new Map();
    dadosGraficoOperador.forEach((item) => {
      const nome = String(item.name || 'Sem operador');
      const atual = mapa.get(nome) || 0;
      mapa.set(nome, atual + Number(item.value || 0));
    });

    return Array.from(mapa.entries())
      .map(([name, value]) => ({
        name: name.length > 25 ? `${name.substring(0, 25)}...` : name,
        value
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  }, [dadosGraficoOperador]);

  const dadosGraficoMes = useMemo(() => {
    const mapa = new Map();

    porMes.forEach((item) => {
      const mes = item.DATA || item.data || 'Sem data';
      const valor = Number(item.TOTAL ?? item.total ?? 0);
      const atual = mapa.get(mes) || 0;
      mapa.set(mes, atual + valor);
    });

    return Array.from(mapa.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => {
      // Converter formato MM/YYYY para Date para ordenação correta considerando ano e mês
      const parseData = (dataStr) => {
        if (!dataStr) return new Date(0);
        // Formato pode ser "01/2026" ou "2026-01" ou similar
        if (dataStr.includes('/')) {
          const [mes, ano] = dataStr.split('/');
          return new Date(parseInt(ano), parseInt(mes) - 1, 1);
        } else if (dataStr.includes('-')) {
          const [ano, mes] = dataStr.split('-');
          return new Date(parseInt(ano), parseInt(mes) - 1, 1);
        }
        return new Date(0);
      };
      
      const dateA = parseData(a.name);
      const dateB = parseData(b.name);
      return dateA - dateB;
    });
  }, [porMes]);

  // Dados para gráficos de pizza dos rankings
  const rankingProdutosAgregado = useMemo(() => {
    const mapa = new Map();
    topProdutos.forEach((item) => {
      const produto = item.PRODUTO || item.produto || 'Sem produto';
      const atual = mapa.get(produto) || 0;
      mapa.set(produto, atual + Number(item.TOTAL ?? item.total ?? 0));
    });

    return Array.from(mapa.entries())
      .map(([PRODUTO, TOTAL]) => ({ PRODUTO, TOTAL }))
      .sort((a, b) => b.TOTAL - a.TOTAL);
  }, [topProdutos]);

  const rankingFornecedoresAgregado = useMemo(() => {
    const mapa = new Map();
    topFornecedores.forEach((item) => {
      const fornecedor = item.FORNECEDOR || item.fornecedor || 'Sem fornecedor';
      const atual = mapa.get(fornecedor) || 0;
      mapa.set(fornecedor, atual + Number(item.TOTAL ?? item.total ?? 0));
    });

    return Array.from(mapa.entries())
      .map(([FORNECEDOR, TOTAL]) => ({ FORNECEDOR, TOTAL }))
      .sort((a, b) => b.TOTAL - a.TOTAL);
  }, [topFornecedores]);

  const dadosGraficoProdutosAgregado = useMemo(
    () => rankingProdutosAgregado.slice(0, 10).map((item) => ({ name: item.PRODUTO, value: item.TOTAL })),
    [rankingProdutosAgregado]
  );

  const dadosGraficoFornecedoresAgregado = useMemo(
    () => rankingFornecedoresAgregado.slice(0, 10).map((item) => ({ name: item.FORNECEDOR, value: item.TOTAL })),
    [rankingFornecedoresAgregado]
  );

  const chartTheme = useMemo(() => ({
    gridStroke: 'var(--border)',
    axisStroke: 'var(--border)',
    tickColor: 'var(--text-muted)',
    barFill: 'var(--accent)',
    tooltipBg: 'var(--surface)',
    tooltipBorder: 'var(--border)',
    tooltipText: 'var(--text-primary)',
    tooltipLabel: 'var(--text-muted)'
  }), []);

  const rankingLegendColors = useMemo(() => ([
    'var(--accent)',
    'color-mix(in srgb, var(--accent) 82%, var(--surface) 18%)',
    'color-mix(in srgb, var(--accent) 70%, var(--surface) 30%)',
    'color-mix(in srgb, var(--accent) 58%, var(--surface) 42%)',
    'color-mix(in srgb, var(--accent) 46%, var(--surface) 54%)',
    'color-mix(in srgb, var(--accent) 36%, var(--surface) 64%)',
    'color-mix(in srgb, var(--accent) 28%, var(--surface) 72%)',
    'color-mix(in srgb, var(--accent) 22%, var(--surface) 78%)',
    'color-mix(in srgb, var(--accent) 16%, var(--surface) 84%)',
    'color-mix(in srgb, var(--accent) 10%, var(--surface) 90%)'
  ]), []);

  return (
    <div className="space-y-4 sm:space-y-6">
      {notification && (
        <NotificationToast
          message={notification.message}
          type={notification.type}
          duration={4000}
          onClose={() => setNotification(null)}
        />
      )}
      <div className="rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)]/95 shadow-[0_8px_28px_rgba(15,23,42,0.08)] px-4 py-4 sm:px-5 sm:py-5">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <h3 className="text-sm uppercase tracking-wide font-semibold text-[color:var(--text-muted)]">Período</h3>
          <PeriodoDisplay start={range.start} end={range.end} />
          {loading ? (
            <div className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium bg-[color:var(--surface-muted)] text-[color:var(--accent)]">
              <svg className="h-4 w-4 mr-1 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>
              Atualizando...
            </div>
          ) : (
            ultimaAtualizacao && (
              <div className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium bg-[color:var(--surface-muted)] text-[color:var(--text-muted)]">Atualizado: {ultimaAtualizacao.toLocaleTimeString('pt-BR')}</div>
            )
          )}
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={limparCacheERecarregar}
            disabled={limparCacheLoading || loading}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-[color:var(--border)] bg-[color:var(--surface-muted)] px-3.5 text-sm font-medium text-[color:var(--text-primary)] shadow-sm transition-all duration-200 hover:brightness-[1.03] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:ring-offset-2 focus:ring-offset-[color:var(--surface)] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className={`h-4 w-4 ${limparCacheLoading ? 'animate-spin' : ''}`} />
            {limparCacheLoading ? 'Recarregando...' : 'Recarregar Dados'}
          </button>
          <div ref={dropdownCidadesRef} className="relative inline-block">
            <button
              type="button"
              onClick={() => {
                if (dropdownCidadesAberto) {
                  const selecoesIguais = cidadesSelecionadas.length === cidadesSelecionadasDraft.length
                    && cidadesSelecionadas.every((item) => cidadesSelecionadasDraft.includes(item));
                  if (!selecoesIguais) {
                    setCidadesSelecionadas(cidadesSelecionadasDraft);
                  }
                  setDropdownCidadesAberto(false);
                  return;
                }
                setCidadesSelecionadasDraft(cidadesSelecionadas);
                setDropdownCidadesAberto(true);
              }}
              className={`w-full sm:w-auto inline-flex h-10 items-center gap-2 rounded-lg border px-3.5 text-sm font-medium shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:ring-offset-2 focus:ring-offset-[color:var(--surface)] ${
                (dropdownCidadesAberto ? cidadesSelecionadasDraft : cidadesSelecionadas).length > 0
                  ? 'border-[color:var(--accent)] bg-[color:var(--accent)] text-white'
                  : 'border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text-primary)] hover:bg-[color:var(--surface-muted)]'
              }`}
            >
              <MapPin className="h-4 w-4" />
              <span className="truncate">
                {(dropdownCidadesAberto ? cidadesSelecionadasDraft : cidadesSelecionadas).length === 0
                  ? 'Filtrar por Cidade'
                  : `${(dropdownCidadesAberto ? cidadesSelecionadasDraft : cidadesSelecionadas).length} cidade${(dropdownCidadesAberto ? cidadesSelecionadasDraft : cidadesSelecionadas).length > 1 ? 's' : ''} selecionada${(dropdownCidadesAberto ? cidadesSelecionadasDraft : cidadesSelecionadas).length > 1 ? 's' : ''}`
                }
              </span>
              {(dropdownCidadesAberto ? cidadesSelecionadasDraft : cidadesSelecionadas).length > 0 && (
                <span className="inline-flex items-center rounded-full bg-[color:var(--accent)] px-2 py-0.5 text-xs font-medium text-white">
                  {(dropdownCidadesAberto ? cidadesSelecionadasDraft : cidadesSelecionadas).length}
                </span>
              )}
              <ChevronDown className="h-4 w-4 text-[color:var(--text-muted)]" />
            </button>

            {dropdownCidadesAberto && (
              <div className="absolute z-50 mt-2 w-72 rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] shadow-[0_16px_40px_rgba(2,6,23,0.28)] focus:outline-none right-0">
                <div className="p-3 border-b border-[color:var(--border)] bg-[color:var(--surface-muted)]/35 rounded-t-xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold tracking-tight text-[color:var(--text-primary)]">Filtrar por Cidade</span>
                    {cidadesSelecionadasDraft.length > 0 && (
                      <button
                        onClick={() => {
                          setCidadesSelecionadasDraft([]);
                          setBuscaCidade('');
                        }}
                        className="text-xs font-medium text-[color:var(--accent)] hover:opacity-80"
                      >
                        Limpar
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-[color:var(--text-muted)]" />
                    <input
                      type="text"
                      value={buscaCidade}
                      onChange={(e) => setBuscaCidade(e.target.value)}
                      placeholder="Buscar cidade..."
                      className="w-full pl-8 pr-2 py-2 text-sm border border-[color:var(--border)] rounded-lg bg-[color:var(--surface)] text-[color:var(--text-primary)] placeholder-[color:var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:border-[color:var(--accent)]"
                      onClick={(e) => e.stopPropagation()}
                    />
                    {buscaCidade && (
                      <button
                        onClick={() => setBuscaCidade('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)] hover:text-[color:var(--text-muted)]"
                        type="button"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="max-h-64 overflow-auto p-2">
                  {cidadesFiltradas.length > 0 ? (
                    cidadesFiltradas.map((cidade) => {
                      const isSelected = cidadesSelecionadasDraft.includes(cidade);
                      return (
                        <label
                          key={cidade}
                          className={`flex items-center px-3 py-2 text-sm hover:bg-[color:var(--surface-muted)] cursor-pointer rounded-lg transition-colors ${
                            isSelected ? 'bg-[color:var(--surface-muted)]' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {
                              setCidadesSelecionadasDraft((prev) => (
                                prev.includes(cidade)
                                  ? prev.filter((c) => c !== cidade)
                                  : [...prev, cidade]
                              ));
                            }}
                            className="h-4 w-4 text-[color:var(--accent)] focus:ring-[color:var(--accent)] border-[color:var(--border)] rounded"
                          />
                          <span className={`ml-2 flex-1 ${isSelected ? 'font-medium text-[color:var(--text-primary)]' : 'text-[color:var(--text-muted)]'}`}>
                            {cidade}
                          </span>
                        </label>
                      );
                    })
                  ) : (
                    <div className="px-3 py-2 text-sm text-[color:var(--text-muted)]">
                      {buscaCidade ? 'Nenhuma cidade encontrada' : 'Nenhuma cidade disponível'}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="h-10">
            <DateRangeFilterBlur
              value={range}
              loading={loading}
              onChange={(r) => {
                setRange(r);
              }}
            />
          </div>
        </div>
      </div>
      </div>
      {/* Toggle para alternar entre Entrada e Saída */}
      <div className="bg-[color:var(--surface)] rounded-xl border border-[color:var(--border)] shadow-[0_8px_28px_rgba(15,23,42,0.08)] p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <h3 className="text-lg font-semibold text-[color:var(--text-primary)]">
            Tipo de Movimentação
          </h3>
          <div className="flex items-center justify-center sm:justify-end space-x-4">
            <span className={`text-sm font-medium ${tipoAtivo === 'entrada' ? 'text-[color:var(--accent)]' : 'text-[color:var(--text-muted)]'}`}>
              Entrada
            </span>
        <button
          onClick={() => setTipoAtivo(tipoAtivo === 'entrada' ? 'saida' : 'entrada')}
          className={`relative inline-flex h-6 w-11 items-center rounded-full border transition-colors focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:ring-offset-2 ${
            tipoAtivo === 'saida'
              ? 'bg-[color:var(--accent)] border-transparent'
              : 'bg-[color:var(--surface-muted)] border-[color:var(--border)]'
          }`}
          aria-pressed={tipoAtivo === 'saida'}
        >
          <span
            className={`${
              tipoAtivo === 'saida' ? 'translate-x-6' : 'translate-x-1'
            } inline-block h-4 w-4 transform rounded-full bg-white shadow-sm ring-1 ring-black/10 transition-transform`}
          />
        </button>
            <span className={`text-sm font-medium ${tipoAtivo === 'saida' ? 'text-[color:var(--accent)]' : 'text-[color:var(--text-muted)]'}`}>
              Saída
            </span>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title={`Total de Movimentações (${tipoLabel})`}
          value={kpisFiltrados.total_nf?.toLocaleString('pt-BR') || '0'}
          icon={FileText}
          color="blue"
          className="min-h-[132px]"
        />
        <KPICard
          title={tipoAtivo === 'entrada' ? 'Total de Entradas' : 'Total de Saídas'}
          value={totalMovimentacao.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          icon={Package}
          color="green"
          className="min-h-[132px]"
        />
        <KPICard
          title={`Valor Total (${tipoLabel})`}
          value={`R$ ${parseFloat(kpisFiltrados.total_valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          icon={DollarSign}
          color="purple"
          className="min-h-[132px]"
        />
        <KPICard
          title={`Média por Movimentação (${tipoLabel})`}
          value={`R$ ${(parseFloat(kpisFiltrados.total_valor || 0) / (kpisFiltrados.total_nf || 1)).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          icon={TrendingUp}
          color="orange"
          className="min-h-[132px]"
        />
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6">
        <CustomBarChart
          data={dadosGraficoDocumentoAgregado}
          title={`Movimentações por Documento (${tipoLabel})`}
          dataKey="value"
          nameKey="name"
          color="var(--accent)"
          maxHeight="max-h-80"
          allowLabelWrap={true}
          maxLabelLines={3}
        />
        <CustomBarChart
          data={dadosGraficoOperadorAgregado}
          title={`Movimentações por Operador (${tipoLabel})`}
          dataKey="value"
          nameKey="name"
          color="var(--status-good)"
          maxHeight="max-h-80"
          allowLabelWrap={true}
          maxLabelLines={3}
        />
      </div>

      {/* Gráfico de Barras - Por Mês */}
      <div className="bg-[color:var(--surface)] rounded-xl border border-[color:var(--border)] shadow-[0_8px_28px_rgba(15,23,42,0.08)] p-4 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold tracking-tight text-[color:var(--text-primary)] mb-4">
          Movimentações por Mês ({tipoLabel})
          <span className="ml-2 text-sm text-[color:var(--text-muted)]">
            ({dadosGraficoMes.length} registros)
          </span>
        </h3>
        
        {dadosGraficoMes.length > 0 ? (
          <div className="h-80 sm:h-96">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={dadosGraficoMes}
                margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 12,
                }}
              >
                <CartesianGrid stroke={chartTheme.gridStroke} strokeDasharray="3 3" />
                <XAxis 
                  dataKey="name" 
                  angle={-45}
                  textAnchor="end"
                  height={72}
                  fontSize={11}
                  interval={0}
                  tickMargin={8}
                  stroke={chartTheme.axisStroke}
                  tick={{ fill: chartTheme.tickColor }}
                />
                <YAxis stroke={chartTheme.axisStroke} tick={{ fill: chartTheme.tickColor }} />
                <Tooltip 
                  formatter={(value) => [Number(value || 0).toLocaleString('pt-BR'), 'Movimentações']}
                  labelFormatter={(label) => `Mês: ${label}`}
                  cursor={{ fill: 'rgba(148, 163, 184, 0.16)' }}
                  contentStyle={{
                    backgroundColor: chartTheme.tooltipBg,
                    border: `1px solid ${chartTheme.tooltipBorder}`,
                    borderRadius: '10px',
                    color: chartTheme.tooltipText,
                    boxShadow: '0 8px 28px rgba(0, 0, 0, 0.25)'
                  }}
                  itemStyle={{ color: chartTheme.tooltipText, fontWeight: 600 }}
                  labelStyle={{ color: chartTheme.tooltipLabel }}
                />
                <Bar 
                  dataKey="value" 
                  fill={chartTheme.barFill}
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="text-center py-12 text-[color:var(--text-muted)]">
            <Package className="w-12 h-12 mx-auto mb-4 text-[color:var(--text-muted)]/50" />
            <p>Nenhum dado disponível para este período</p>
          </div>
        )}
      </div>

      {/* Rankings com Classificação */}
      <div className="bg-[color:var(--surface)] rounded-xl border border-[color:var(--border)] shadow-[0_8px_28px_rgba(15,23,42,0.08)] p-4 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold tracking-tight text-[color:var(--text-primary)] mb-4 flex items-center">
          <Package className="w-5 h-5 mr-2 text-[color:var(--accent)]" />
          Classificação de Rankings
        </h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
          <button
            onClick={() => setClassificacaoRanking('produtos')}
            className={`flex items-center justify-center p-3 rounded-lg text-sm font-medium transition-colors duration-200 border
              ${classificacaoRanking === 'produtos'
                ? 'bg-[color:var(--accent)] text-white border-[color:var(--accent)]'
                : 'bg-[color:var(--surface-muted)] text-[color:var(--text-primary)] border-[color:var(--border)] hover:brightness-[1.03]'
              }`}
          >
            <Package className={`w-4 h-4 mr-2 ${classificacaoRanking === 'produtos' ? 'text-white' : 'text-[color:var(--accent)]'}`} />
            Produtos
          </button>
          <button
            onClick={() => setClassificacaoRanking('fornecedores')}
            className={`flex items-center justify-center p-3 rounded-lg text-sm font-medium transition-colors duration-200 border
              ${classificacaoRanking === 'fornecedores'
                ? 'bg-[color:var(--accent)] text-white border-[color:var(--accent)]'
                : 'bg-[color:var(--surface-muted)] text-[color:var(--text-primary)] border-[color:var(--border)] hover:brightness-[1.03]'
              }`}
          >
            <Users className={`w-4 h-4 mr-2 ${classificacaoRanking === 'fornecedores' ? 'text-white' : 'text-[color:var(--accent)]'}`} />
            Fornecedores
          </button>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 lg:gap-8">
          {/* Lista de Rankings (mesma fonte da legenda) */}
          <div className="flex flex-col">
            <h4 className="text-sm sm:text-base font-semibold text-[color:var(--text-primary)] mb-4">
              {classificacaoRanking === 'produtos' 
                ? 'Produtos Mais Movimentados'
                : 'Top Fornecedores'
              }
              <span className="ml-2 text-[color:var(--text-muted)] text-sm">
                ({classificacaoRanking === 'produtos' ? rankingProdutosAgregado.length : rankingFornecedoresAgregado.length} registros)
              </span>
            </h4>
            <div className="flex-1 min-h-[400px] sm:min-h-[500px] max-h-[500px] overflow-y-auto pr-1">
              <EnhancedLegend
                data={classificacaoRanking === 'produtos' ? dadosGraficoProdutosAgregado : dadosGraficoFornecedoresAgregado}
                dataKey="value"
                nameKey="name"
                colors={rankingLegendColors}
                maxItems={10}
                showToggle={false}
                sortDescending={true}
              />
            </div>
          </div>

          {/* Gráfico de Pizza */}
          <div className="flex flex-col">
            <h4 className="text-sm sm:text-base font-semibold text-[color:var(--text-primary)] mb-4">
              Distribuição {classificacaoRanking === 'produtos' 
                ? 'por Produto'
                : 'por Fornecedor'
              }
            </h4>
            <div className="flex-1 min-h-[400px] sm:min-h-[500px]">
              <CustomPieChart
                data={classificacaoRanking === 'produtos' ? dadosGraficoProdutosAgregado : dadosGraficoFornecedoresAgregado}
                title=""
                dataKey="value"
                nameKey="name"
                height={400}
                showLegend={false}
                sortDescending={true}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Tabelas de Detalhamento */}
      <div className="space-y-4 sm:space-y-6">
        <div>
          <h3 className="text-base sm:text-lg font-semibold tracking-tight text-[color:var(--text-primary)] mb-4">
            Detalhamento Básico ({tipoLabel})
          </h3>
          <div className="overflow-x-auto">
            <DataTable
              data={detalhamentoPaginado}
              columns={columnsDetalhamento}
              loading={isLoading}
              maxHeight="max-h-80 sm:max-h-96"
              emptyMessage="Nenhuma movimentação encontrada"
            />
          </div>
          {totalPages > 1 && (
            <div className="mt-4">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setPage}
                itemsPerPage={itemsPerPage}
                onItemsPerPageChange={setItemsPerPage}
                totalItems={detalhamento.length}
              />
            </div>
          )}
        </div>

        <div>
          <h3 className="text-base sm:text-lg font-semibold tracking-tight text-[color:var(--text-primary)] mb-4">
            Detalhamento Completo ({tipoLabel})
          </h3>
          <div className="overflow-x-hidden">
            <EntradasNFTable
              data={detalhamentoCompleto}
              loading={isLoading}
              maxHeight="max-h-80 sm:max-h-96"
              emptyMessage="Nenhuma movimentação encontrada"
              idField={idField}
              idLabel={idLabel}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MovimentacaoInterna;
